import { useEffect, useId, useState } from "react";
import { ChevronDown } from "lucide-react";

import ReturnRequestEvidenceGallery from "./ReturnRequestEvidenceGallery";
import { buildReturnRefundPolicyView } from "./returnRefundPolicy";

const EXPANDABLE_RETURN_REFUND_STATUSES = new Set(["APPROVED", "REFUNDED", "COMPLETED"]);

function joinClasses(...values) {
  return values.filter(Boolean).join(" ");
}

function normalizeStatusToken(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
}

function getReturnRefundStatusLabel(status) {
  switch (normalizeStatusToken(status)) {
    case "REFUNDED":
      return "Refunded";
    case "APPROVED":
      return "Approved";
    case "REJECTED":
      return "Rejected";
    case "COMPLETED":
      return "Completed";
    case "REQUESTED":
    default:
      return "Requested";
  }
}

function getReturnRefundHeadline(status, refundDestination) {
  const normalizedStatus = normalizeStatusToken(status);
  const normalizedDestination = normalizeStatusToken(refundDestination);

  if (normalizedStatus === "REFUNDED" && normalizedDestination === "WALLET") {
    return "Refund credited to shopper wallet";
  }

  if (normalizedStatus === "REFUNDED") {
    return "Refund completed for this request";
  }

  if (normalizedStatus === "REJECTED") {
    return "Seller declined the request";
  }

  if (normalizedStatus === "APPROVED") {
    return "Seller approved the request";
  }

  if (normalizedStatus === "COMPLETED") {
    return "Return / refund completed";
  }

  return "Seller review in progress";
}

function getRefundDestinationLabel(destination) {
  const normalizedDestination = normalizeStatusToken(destination);
  if (!normalizedDestination) return null;
  if (normalizedDestination === "WALLET") return "Shopper Credit / Wallet";

  return normalizedDestination
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatDisplayValue(value, formatter) {
  if (!value) return "-";

  if (typeof formatter === "function") {
    const formatted = formatter(value);
    if (typeof formatted === "string" && formatted.trim()) {
      return formatted;
    }
  }

  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return date.toLocaleString("en-PH", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return String(value);
}

function hasRenderableValue(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === "number") return Number.isFinite(value);
  return String(value).trim().length > 0;
}

function getPolicyBadgeClasses(tone) {
  switch (tone) {
    case "sky":
      return "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900/40 dark:bg-sky-950/30 dark:text-sky-200";
    case "amber":
      return "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200";
    case "rose":
      return "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200";
    case "orange":
    default:
      return "border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-900/40 dark:bg-orange-950/30 dark:text-orange-200";
  }
}

export default function ReturnRefundStatusCard({
  returnRefund,
  returnRequest,
  orderReference = null,
  returnAddress = null,
  formatAmount,
  formatDateTime,
  collapsible = false,
  defaultExpanded = false,
  className = "",
}) {
  const hasRequest = Boolean(returnRefund?.requested || returnRequest || returnRefund?.status);
  const status = returnRefund?.status || returnRequest?.status || null;
  const normalizedStatus = normalizeStatusToken(status);
  const canExpand = hasRequest && EXPANDABLE_RETURN_REFUND_STATUSES.has(normalizedStatus);
  const [expanded, setExpanded] = useState(Boolean(defaultExpanded));
  const contentId = useId();
  const [instructionsExpanded, setInstructionsExpanded] = useState(false);
  const instructionsId = useId();

  const headline = getReturnRefundHeadline(
    status,
    returnRefund?.refundDestination || returnRequest?.refundDestination
  );
  const statusLabel = getReturnRefundStatusLabel(status);
  const reason = returnRefund?.reason || returnRequest?.reason || "";
  const details = returnRefund?.details || returnRequest?.details || "";
  const message = returnRefund?.message || "";
  const resolutionNote = returnRefund?.resolutionNote || returnRequest?.resolutionNote || "";
  const refundAmount = returnRefund?.refundAmount ?? returnRequest?.refundAmount ?? null;
  const refundDestination = returnRefund?.refundDestination || returnRequest?.refundDestination || "";
  const refundDestinationLabel = getRefundDestinationLabel(refundDestination);
  const requestedAt = returnRefund?.requestedAt || returnRequest?.requestedAt || null;
  const reviewedAt = returnRefund?.reviewedAt || returnRequest?.reviewedAt || null;
  const refundedAt = returnRefund?.refundedAt || returnRequest?.refundedAt || null;
  const deadlineAt = returnRefund?.deadlineAt || returnRequest?.deadlineAt || null;
  const daysRemaining = returnRefund?.daysRemaining ?? returnRequest?.daysRemaining ?? null;
  const evidence = Array.isArray(returnRefund?.evidence) && returnRefund.evidence.length > 0
    ? returnRefund.evidence
    : Array.isArray(returnRequest?.evidence)
      ? returnRequest.evidence
      : [];
  const policy = returnRefund?.policy || returnRequest?.policy || null;
  const policyView = buildReturnRefundPolicyView({
    policy,
    reason,
    status,
    orderReference,
    returnAddress,
  });
  const nextSteps = Array.isArray(policyView?.nextSteps) ? policyView.nextSteps : [];
  const policyHighlights = Array.isArray(policyView?.highlights) ? policyView.highlights : [];
  const policyBadges = Array.isArray(policyView?.badges) ? policyView.badges : [];
  const courierSteps = Array.isArray(policyView?.courierSteps) ? policyView.courierSteps : [];
  const returnAddressInfo = policyView?.returnAddress || null;
  const detailFacts = [
    orderReference ? { label: "Order ID", value: orderReference } : null,
    requestedAt ? { label: "Submitted", value: formatDisplayValue(requestedAt, formatDateTime) } : null,
    reviewedAt ? { label: "Reviewed", value: formatDisplayValue(reviewedAt, formatDateTime) } : null,
    refundedAt ? { label: "Refunded", value: formatDisplayValue(refundedAt, formatDateTime) } : null,
    refundAmount != null && Number(refundAmount) > 0 && typeof formatAmount === "function"
      ? { label: "Refund Amount", value: formatAmount(refundAmount) }
      : null,
    refundDestinationLabel ? { label: "Refund Destination", value: refundDestinationLabel } : null,
    deadlineAt ? { label: "Window Closes", value: formatDisplayValue(deadlineAt, formatDateTime) } : null,
    daysRemaining != null
      ? {
          label: "Days Remaining",
          value: `${Number(daysRemaining)} day${Number(daysRemaining) === 1 ? "" : "s"}`,
        }
      : null,
  ].filter(Boolean);
  const hasInstructionSection = Boolean(returnAddressInfo) || policyHighlights.length > 0 || courierSteps.length > 0;
  const hasExpandedContent =
    detailFacts.length > 0
    || nextSteps.length > 0
    || policyHighlights.length > 0
    || courierSteps.length > 0
    || hasRenderableValue(reason)
    || hasRenderableValue(details)
    || hasRenderableValue(message)
    || hasRenderableValue(resolutionNote)
    || evidence.length > 0;
  const collapseEnabled = collapsible
    ? hasExpandedContent
    : canExpand;
  const showDetails = collapseEnabled ? expanded : true;
  const submittedAtLabel = requestedAt ? formatDisplayValue(requestedAt, formatDateTime) : "";
  const summaryStatusUpdate = [
    resolutionNote,
    message,
    details,
    reason ? `Reason: ${reason}` : "",
    headline,
  ].find((entry) => hasRenderableValue(entry)) || "";
  const summaryMetaChips = [
    orderReference ? `Order ${orderReference}` : "",
    submittedAtLabel ? `Submitted ${submittedAtLabel}` : "",
  ].filter(Boolean);

  useEffect(() => {
    if (!collapseEnabled) {
      setExpanded(true);
      return;
    }

    if (collapsible) {
      setExpanded(Boolean(defaultExpanded));
      return;
    }

    if (!canExpand) {
      setExpanded(true);
      return;
    }

    setExpanded(false);
  }, [canExpand, collapseEnabled, collapsible, defaultExpanded]);

  useEffect(() => {
    if (!showDetails || !hasInstructionSection) {
      setInstructionsExpanded(false);
    }
  }, [showDetails, hasInstructionSection]);

  const summaryCardClasses = joinClasses(
    "rounded-2xl border border-orange-200/70 bg-white/85 px-4 py-3 text-left shadow-sm",
    "dark:border-orange-900/40 dark:bg-neutral-950/40 dark:shadow-none",
    collapseEnabled ? "transition hover:border-orange-300 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 dark:hover:border-orange-800" : ""
  );

  const summaryContent = (
    <>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-orange-600 dark:text-orange-300">
            Return / Refund Request
          </div>
        </div>

        {collapseEnabled ? (
          <div className="inline-flex items-center gap-2 self-start rounded-full border border-orange-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-orange-700 dark:border-orange-900/40 dark:bg-orange-950/40 dark:text-orange-200">
            {expanded ? "Hide details" : "View details"}
            <ChevronDown
              size={14}
              className={joinClasses("shrink-0 transition-transform duration-200", expanded ? "rotate-180" : "rotate-0")}
            />
          </div>
        ) : null}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center rounded-full border border-orange-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-orange-700 shadow-sm dark:border-orange-900/40 dark:bg-orange-950/40 dark:text-orange-200 dark:shadow-none">
          {statusLabel}
        </span>

        {refundAmount != null && Number(refundAmount) > 0 && typeof formatAmount === "function" ? (
          <span className="inline-flex items-center rounded-full border border-orange-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-orange-700 shadow-sm dark:border-orange-900/40 dark:bg-orange-950/40 dark:text-orange-200 dark:shadow-none">
            Refund {formatAmount(refundAmount)}
          </span>
        ) : null}

        {refundDestinationLabel ? (
          <span className="inline-flex items-center rounded-full border border-emerald-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-emerald-700 shadow-sm dark:border-emerald-900/40 dark:bg-emerald-950/40 dark:text-emerald-200 dark:shadow-none">
            {refundDestinationLabel}
          </span>
        ) : null}

        {evidence.length > 0 ? (
          <span className="inline-flex items-center rounded-full border border-orange-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-orange-700 shadow-sm dark:border-orange-900/40 dark:bg-orange-950/40 dark:text-orange-200 dark:shadow-none">
            {evidence.length} evidence file{evidence.length === 1 ? "" : "s"}
          </span>
        ) : null}
      </div>

      {summaryMetaChips.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {summaryMetaChips.map((meta) => (
            <span
              key={meta}
              className="inline-flex max-w-full items-center rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-1 text-[10px] font-semibold text-neutral-600 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300"
            >
              <span className="truncate">{meta}</span>
            </span>
          ))}
        </div>
      ) : null}

      {hasRenderableValue(summaryStatusUpdate) ? (
        <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-neutral-700 dark:text-neutral-200">
          {summaryStatusUpdate}
        </p>
      ) : null}
    </>
  );

  if (!hasRequest) return null;

  return (
    <section
      className={joinClasses(
        "rounded-2xl border border-orange-200/80 bg-gradient-to-br from-orange-50 via-white to-orange-50/70 px-4 py-3.5 shadow-sm",
        "dark:border-orange-900/40 dark:from-orange-950/30 dark:via-neutral-900 dark:to-neutral-900 dark:shadow-none",
        className
      )}
    >
      {collapseEnabled ? (
        <button
          type="button"
          onClick={() => setExpanded((current) => !current)}
          aria-expanded={expanded}
          aria-controls={contentId}
          className={joinClasses(summaryCardClasses, "w-full")}
        >
          {summaryContent}
        </button>
      ) : (
        <div className={summaryCardClasses}>{summaryContent}</div>
      )}

      {hasExpandedContent ? (
        <div
          id={contentId}
          aria-hidden={!showDetails}
          className={joinClasses(
            "overflow-hidden transition-all duration-300 ease-out",
            showDetails ? "mt-3 max-h-[320rem] opacity-100" : "max-h-0 opacity-0"
          )}
        >
          <div className="space-y-3">
            {hasRenderableValue(headline) ? (
              <div className="rounded-2xl border border-orange-200/70 bg-white/85 px-3.5 py-3 dark:border-orange-900/40 dark:bg-neutral-950/40">
                <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-orange-600 dark:text-orange-300">
                  Status Update
                </div>
                <div className="mt-1 break-words text-sm leading-relaxed text-neutral-900 dark:text-neutral-100">
                  {headline}
                </div>
              </div>
            ) : null}

            {detailFacts.length > 0 ? (
              <div className="grid gap-2 sm:grid-cols-2">
                {detailFacts.map((fact) => (
                  <div
                    key={`${fact.label}-${fact.value}`}
                    className="rounded-2xl border border-orange-200/70 bg-white/85 px-3.5 py-3 dark:border-orange-900/40 dark:bg-neutral-950/40"
                  >
                    <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-orange-600 dark:text-orange-300">
                      {fact.label}
                    </div>
                    <div className="mt-1 break-words text-sm leading-relaxed text-neutral-900 dark:text-neutral-100">
                      {fact.value}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            {nextSteps.length > 0 ? (
              <div className="rounded-2xl border border-orange-200/70 bg-white/85 px-3.5 py-3 dark:border-orange-900/40 dark:bg-neutral-950/40">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-orange-600 dark:text-orange-300">
                    What Happens Next
                  </div>
                  {policyBadges.map((badge) => (
                    <span
                      key={`${badge.label}-${badge.tone}`}
                      className={joinClasses(
                        "inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-semibold",
                        getPolicyBadgeClasses(badge.tone)
                      )}
                    >
                      {badge.label}
                    </span>
                  ))}
                </div>

                <div className="mt-3 space-y-2">
                  {nextSteps.map((step) => (
                    <div key={step} className="flex items-start gap-2.5 text-sm leading-relaxed text-neutral-700 dark:text-neutral-200">
                      <span className="mt-[0.45rem] h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500 dark:bg-orange-300" />
                      <span className="break-words">{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {hasInstructionSection ? (
              <div className="rounded-2xl border border-orange-200/70 bg-white/85 px-3.5 py-3 dark:border-orange-900/40 dark:bg-neutral-950/40">
                <button
                  type="button"
                  onClick={() => setInstructionsExpanded((current) => !current)}
                  aria-expanded={instructionsExpanded}
                  aria-controls={instructionsId}
                  className="w-full text-left"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-orange-600 dark:text-orange-300">
                        Return Terms & Shipping Instructions
                      </div>
                      <div className="mt-1 text-sm leading-relaxed text-neutral-700 dark:text-neutral-200">
                        {returnAddressInfo?.isAvailable
                          ? "The business return address and courier steps are available here."
                          : "Review the courier steps here. If the business return address is still missing, confirm it through chat before shipping anything."}
                      </div>
                    </div>

                    <div className="inline-flex items-center gap-2 self-start rounded-full border border-orange-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-orange-700 dark:border-orange-900/40 dark:bg-orange-950/40 dark:text-orange-200">
                      {instructionsExpanded ? "Hide details" : "View details"}
                      <ChevronDown
                        size={14}
                        className={joinClasses("shrink-0 transition-transform duration-200", instructionsExpanded ? "rotate-180" : "rotate-0")}
                      />
                    </div>
                  </div>

                  {policyBadges.length > 0 ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {policyBadges.slice(0, 2).map((badge) => (
                        <span
                          key={`${badge.label}-${badge.tone}`}
                          className={joinClasses(
                            "inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-semibold",
                            getPolicyBadgeClasses(badge.tone)
                          )}
                        >
                          {badge.label}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </button>

                <div
                  id={instructionsId}
                  aria-hidden={!instructionsExpanded}
                  className={joinClasses(
                    "overflow-hidden transition-all duration-300 ease-out",
                    instructionsExpanded ? "mt-3 max-h-[220rem] opacity-100" : "max-h-0 opacity-0"
                  )}
                >
                  <div className="space-y-3">
                    {returnAddressInfo ? (
                      <div className="rounded-2xl border border-orange-200/70 bg-orange-50/60 px-3.5 py-3 dark:border-orange-900/40 dark:bg-orange-950/20">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-orange-600 dark:text-orange-300">
                          {returnAddressInfo.title}
                        </div>
                        <div className="mt-1 break-words text-sm leading-relaxed text-neutral-900 dark:text-neutral-100">
                          {returnAddressInfo.body}
                        </div>

                        {Array.isArray(returnAddressInfo.lines) && returnAddressInfo.lines.length > 0 ? (
                          <div className="mt-3 rounded-2xl border border-orange-200/60 bg-white/85 px-3 py-2.5 dark:border-orange-900/40 dark:bg-neutral-950/40">
                            {returnAddressInfo.lines.map((line) => (
                              <div key={line} className="break-words text-sm font-medium text-neutral-900 dark:text-neutral-100">
                                {line}
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ) : null}

                    {policyHighlights.length > 0 ? (
                      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        {policyHighlights.map((highlight) => (
                          <div
                            key={highlight.title}
                            className="rounded-2xl border border-orange-200/70 bg-white/85 px-3.5 py-3 dark:border-orange-900/40 dark:bg-neutral-950/40"
                          >
                            <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-orange-600 dark:text-orange-300">
                              {highlight.title}
                            </div>
                            <div className="mt-1 break-words text-sm leading-relaxed text-neutral-900 dark:text-neutral-100">
                              {highlight.body}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}

                    {courierSteps.length > 0 ? (
                      <div className="rounded-2xl border border-orange-200/70 bg-white/85 px-3.5 py-3 dark:border-orange-900/40 dark:bg-neutral-950/40">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-orange-600 dark:text-orange-300">
                          Manual Courier Return Steps
                        </div>
                        <div className="mt-2 space-y-2">
                          {courierSteps.map((step) => (
                            <div key={step} className="flex items-start gap-2.5 text-sm leading-relaxed text-neutral-700 dark:text-neutral-200">
                              <span className="mt-[0.45rem] h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500 dark:bg-orange-300" />
                              <span className="break-words">{step}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}

            {hasRenderableValue(reason) || hasRenderableValue(details) ? (
              <div className="grid gap-2 sm:grid-cols-2">
                {hasRenderableValue(reason) ? (
                  <div className="rounded-2xl border border-orange-200/70 bg-white/85 px-3.5 py-3 dark:border-orange-900/40 dark:bg-neutral-950/40">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-orange-600 dark:text-orange-300">
                      Reason
                    </div>
                    <div className="mt-1 break-words text-sm leading-relaxed text-neutral-900 dark:text-neutral-100">
                      {reason}
                    </div>
                  </div>
                ) : null}

                {hasRenderableValue(details) ? (
                  <div className="rounded-2xl border border-orange-200/70 bg-white/85 px-3.5 py-3 dark:border-orange-900/40 dark:bg-neutral-950/40">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-orange-600 dark:text-orange-300">
                      Details
                    </div>
                    <div className="mt-1 break-words text-sm leading-relaxed text-neutral-900 dark:text-neutral-100">
                      {details}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {hasRenderableValue(message) ? (
              <div className="rounded-2xl border border-orange-200/70 bg-white/85 px-3.5 py-3 break-words text-sm leading-relaxed text-orange-800 dark:border-orange-900/40 dark:bg-neutral-950/40 dark:text-orange-100">
                {message}
              </div>
            ) : null}

            {evidence.length > 0 ? (
              <ReturnRequestEvidenceGallery media={evidence} />
            ) : null}

            {hasRenderableValue(resolutionNote) ? (
              <div className="rounded-2xl border border-orange-200/70 bg-white/85 px-3.5 py-3 dark:border-orange-900/40 dark:bg-neutral-950/40">
                <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-orange-600 dark:text-orange-300">
                  Seller Note
                </div>
                <div className="mt-1 break-words text-sm leading-relaxed text-neutral-900 dark:text-neutral-100">
                  {resolutionNote}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}
